import parse from "html-react-parser"
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent } from "react"

import { refreshJWT } from "shared/lib/graphql"

import iconArrowUp from "images/icons/icon-arrow-up.svg?raw"
import iconSquareStop from "images/icons/icon-square-stop.svg?raw"

import type { ChatItem, Intention, UIItem, UpgradeRequest, UseChatOptions } from "./types"
import { useChat } from "./use_chat"
import BackToLatestToast from "./back_to_latest_toast"
import AssistantMessage from "./messages/assistant_message"
import SystemNotice from "./messages/system_notice"
import ThinkingIndicator from "./messages/thinking_indicator"
import ToolIndicator from "./messages/tool_indicator"
import ToolPulse from "./messages/tool_pulse"
import UserBubble from "./messages/user_bubble"
import UIEventSlot from "./ui_events/ui_event_slot"
import styles from "./conversation.module.scss"

interface Props {
  initialMessage?: string
  intention: Intention
  onAppletCreated?: UseChatOptions["onAppletCreated"]
  onSessionStarted?: UseChatOptions["onSessionStarted"]
  onSessionEnded?: UseChatOptions["onSessionEnded"]
  onTurnCompleted?: UseChatOptions["onTurnCompleted"]
  getJWT?: () => Promise<string>
  username?: string
  onUpgradeRequest?: (request: UpgradeRequest) => void
}

async function defaultGetJWT(): Promise<string> {
  const app = (window as unknown as { App: { userJWT: string; jwtExp?: number } }).App
  if (app?.jwtExp && Date.now() / 1000 + 60 > app.jwtExp) {
    await refreshJWT()
  }
  return app.userJWT
}

function isFloatingCard(item: ChatItem): item is UIItem {
  // Settled permission cards render inline in the thread as a disabled record, not in the dock.
  return item.kind === "ui" && item.ui.type !== "applet_success_card" && !item.response
}

function scrollToBottom(el: HTMLElement | null, behavior: ScrollBehavior) {
  if (!el) return
  if (behavior === "smooth" && typeof el.scrollTo === "function") {
    el.scrollTo({ top: el.scrollHeight, behavior })
  } else {
    el.scrollTop = el.scrollHeight
  }
}

const ANCHOR_TOP_GAP = 24

function scrollAnchorToTop(container: HTMLElement, anchor: HTMLElement, behavior: ScrollBehavior) {
  const top = container.scrollTop + anchor.getBoundingClientRect().top - container.getBoundingClientRect().top - ANCHOR_TOP_GAP
  if (behavior === "smooth" && typeof container.scrollTo === "function") {
    container.scrollTo({ top, behavior })
  } else {
    container.scrollTop = top
  }
}

function sizeSpacer(container: HTMLElement, spacer: HTMLElement, anchor: HTMLElement) {
  const occupied = spacer.getBoundingClientRect().top - anchor.getBoundingClientRect().top
  spacer.style.height = `${Math.max(0, container.clientHeight - ANCHOR_TOP_GAP - occupied)}px`
}

const Conversation = ({
  initialMessage,
  intention,
  onAppletCreated,
  onSessionStarted,
  onSessionEnded,
  onTurnCompleted,
  getJWT = defaultGetJWT,
  username,
  onUpgradeRequest,
}: Props) => {
  const chat = useChat({ intention, getJWT, onAppletCreated, onSessionStarted, onSessionEnded, onTurnCompleted })
  const [draft, setDraft] = useState("")

  const sentinelRef = useRef<HTMLDivElement>(null)
  const messagesRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const [isPinned, setIsPinned] = useState(true)
  const isPinnedRef = useRef(true)
  const [hasNewSinceUnpin, setHasNewSinceUnpin] = useState(false)
  const lastItemCountRef = useRef(0)
  const spacerRef = useRef<HTMLDivElement>(null)
  const anchorIdRef = useRef<string | null>(null)
  const userScrolledRef = useRef(false)

  useEffect(() => {
    const root = messagesRef.current
    if (!root) return
    const mark = () => {
      userScrolledRef.current = true
    }
    root.addEventListener("wheel", mark, { passive: true })
    root.addEventListener("touchmove", mark, { passive: true })
    return () => {
      root.removeEventListener("wheel", mark)
      root.removeEventListener("touchmove", mark)
    }
  }, [])

  const layoutAnchor = useCallback((scrollBehavior?: ScrollBehavior) => {
    const container = messagesRef.current
    const spacer = spacerRef.current
    const id = anchorIdRef.current
    if (!container || !spacer || !id) return false
    const anchor = container.querySelector<HTMLElement>(`[data-item-id="${id}"]`)
    if (!anchor) return false
    sizeSpacer(container, spacer, anchor)
    if (scrollBehavior) scrollAnchorToTop(container, anchor, scrollBehavior)
    return true
  }, [])

  useEffect(() => {
    const sentinel = sentinelRef.current
    const root = messagesRef.current
    if (!sentinel || !root || typeof IntersectionObserver === "undefined") return
    const observer = new IntersectionObserver(
      entries => {
        const entry = entries[0]
        if (!entry) return
        const pinned = entry.isIntersecting
        isPinnedRef.current = pinned
        setIsPinned(pinned)
        if (pinned) setHasNewSinceUnpin(false)
      },
      { root, threshold: 0 }
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    const root = messagesRef.current
    const content = contentRef.current
    if (!root || !content || typeof ResizeObserver === "undefined") return
    const observer = new ResizeObserver(() => {
      if (layoutAnchor()) return
      if (isPinnedRef.current) scrollToBottom(root, "auto")
    })
    observer.observe(content)
    return () => observer.disconnect()
  }, [layoutAnchor])

  // Dep is `chat.items` (not length) so streaming token updates also tick this.
  useLayoutEffect(() => {
    const grew = chat.items.length > lastItemCountRef.current
    lastItemCountRef.current = chat.items.length
    const last = chat.items[chat.items.length - 1]
    if (grew && last?.kind === "user") {
      anchorIdRef.current = last.id
      userScrolledRef.current = false
      layoutAnchor("smooth")
      return
    }
    if (layoutAnchor()) {
      if (grew && userScrolledRef.current && !isPinned) setHasNewSinceUnpin(true)
      return
    }
    if (isPinned) {
      scrollToBottom(messagesRef.current, "auto")
    } else if (grew) {
      setHasNewSinceUnpin(true)
    }
  }, [chat.items, isPinned, layoutAnchor])

  useEffect(() => {
    if (chat.phase === "done" || chat.phase === "idle") anchorIdRef.current = null
  }, [chat.phase])

  const scrollToLatest = useCallback(() => {
    anchorIdRef.current = null
    userScrolledRef.current = false
    if (spacerRef.current) spacerRef.current.style.height = "0px"
    scrollToBottom(messagesRef.current, "smooth")
    setHasNewSinceUnpin(false)
  }, [])

  useLayoutEffect(() => {
    const textarea = textareaRef.current
    if (!textarea) return
    textarea.style.height = "auto"
    textarea.style.height = `${textarea.scrollHeight}px`
  }, [draft])

  const initialSentRef = useRef(false)
  useEffect(() => {
    if (initialSentRef.current) return
    if (!initialMessage) return
    initialSentRef.current = true
    void chat.send(initialMessage)
    // chat.send is stable from useChat; intentionally not in deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialMessage])

  const isStreaming =
    chat.phase === "connecting" ||
    chat.phase === "thinking" ||
    chat.phase === "streaming" ||
    chat.phase === "tool_running"

  const hasCreatedApplet = chat.items.some(it => it.kind === "ui" && it.ui.type === "applet_success_card")
  const composerPlaceholder = hasCreatedApplet ? "Describe what you'd like to build or ask me a question" : "Reply ..."

  // Refocus on the streaming → idle edge only — never on mount.
  const wasStreamingRef = useRef(isStreaming)
  useEffect(() => {
    const wasStreaming = wasStreamingRef.current
    wasStreamingRef.current = isStreaming
    if (wasStreaming && !isStreaming) textareaRef.current?.focus()
  }, [isStreaming])

  const handleSubmit = useCallback(() => {
    const trimmed = draft.trim()
    if (!trimmed || isStreaming) return
    setDraft("")
    void chat.send(trimmed)
  }, [draft, isStreaming, chat])

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault()
        handleSubmit()
      }
    },
    [handleSubmit]
  )

  const floatingCards = chat.items.filter(isFloatingCard)
  const hasActiveCard = floatingCards.length > 0
  const dockClassName = hasActiveCard
    ? `${styles["composer-dock"]} ${styles["composer-dock--cards-only"]}`
    : styles["composer-dock"]

  return (
    <section className={styles["conversation"]} data-testid="conversation">
      <div className={styles["messages"]} ref={messagesRef} role="log" aria-live="polite">
        <div className={styles["messages-content"]} ref={contentRef}>
          {chat.capWarning ? (
            <SystemNotice
              message={`Conversation is getting long (${chat.capWarning.usage.toLocaleString()} / ${chat.capWarning.cap.toLocaleString()} tokens). You may want to start a new chat soon.`}
              severity="warning"
              recoverable
            />
          ) : null}
          {chat.items
            .filter(item => !isFloatingCard(item))
            .map(item =>
              renderItem(item, { onResetChat: chat.reset, onRespondToUI: chat.respondToUI, username, onUpgradeRequest })
            )}
          {chat.phase === "thinking" || chat.phase === "connecting" ? <ThinkingIndicator /> : null}
          {chat.phase === "tool_running" ? <ToolPulse /> : null}
          <div ref={spacerRef} aria-hidden="true" />
          <div ref={sentinelRef} aria-hidden="true" />
        </div>
      </div>
      <div className={dockClassName}>
        {!isPinned && hasNewSinceUnpin ? <BackToLatestToast onClick={scrollToLatest} /> : null}
        {floatingCards.map(item => (
          <div key={item.id} className={styles["floating-card"]}>
            <UIEventSlot
              ui={item.ui}
              username={username}
              onRespond={chat.respondToUI}
              onUpgradeRequest={onUpgradeRequest}
            />
          </div>
        ))}
        {hasActiveCard ? null : (
          <div
            className={styles["composer"]}
            data-testid="composer"
            onMouseDown={e => {
              // Forward focus to the textarea when the click lands on the
              // composer's dead space (not on the textarea or a button).
              if (e.target === e.currentTarget) {
                e.preventDefault()
                textareaRef.current?.focus()
              }
            }}
          >
            <textarea
              ref={textareaRef}
              aria-label="Reply"
              placeholder={composerPlaceholder}
              rows={1}
              value={draft}
              disabled={isStreaming}
              onChange={e => setDraft(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            <div
              className={styles["composer-actions"]}
              onMouseDown={e => {
                if (e.target === e.currentTarget) {
                  e.preventDefault()
                  textareaRef.current?.focus()
                }
              }}
            >
              {isStreaming ? (
                <button type="button" aria-label="Stop" className={styles["composer-button"]} onClick={chat.stop}>
                  {parse(iconSquareStop)}
                </button>
              ) : (
                <button
                  type="button"
                  aria-label="Send"
                  className={styles["composer-button"]}
                  disabled={!draft.trim()}
                  onClick={handleSubmit}
                >
                  {parse(iconArrowUp)}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  )
}

interface RenderContext {
  onResetChat: () => void
  onRespondToUI: (
    uiId: string,
    actionId: string,
    value: unknown,
    displayText?: string,
    ingredients?: Record<string, string>
  ) => Promise<void>
  username?: string
  onUpgradeRequest?: (request: UpgradeRequest) => void
}

function renderItem(item: ChatItem, ctx: RenderContext) {
  switch (item.kind) {
    case "user":
      return <UserBubble key={item.id} itemId={item.id} text={item.text} ingredients={item.ingredients} />
    case "assistant":
      return <AssistantMessage key={item.id} text={item.text} />
    case "tool":
      return <ToolIndicator key={item.id} tool={item.tool} phase={item.phase} error={item.error} />
    case "notice":
      return (
        <SystemNotice
          key={item.id}
          message={item.message}
          severity={item.severity}
          recoverable={item.recoverable}
          onResetChat={ctx.onResetChat}
        />
      )
    case "ui":
      // Cards settled inline in the thread get breathing room from the messages above and below.
      return (
        <div key={item.id} className={styles["chat-card"]}>
          <UIEventSlot
            ui={item.ui}
            response={item.response}
            username={ctx.username}
            onRespond={ctx.onRespondToUI}
            onUpgradeRequest={ctx.onUpgradeRequest}
          />
        </div>
      )
    default:
      return null
  }
}

export default Conversation
